import { REMINDERS_PAUSED_REASONS } from '@shared/core/billing'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useAddUserEvent } from 'src/modules/user-events/use-add-event'
import { alertCampaigns } from './alert-campaigns'
import {
  getAlertsCoordinatorCooldownRemainingMs,
  recordAlertsCoordinatorShownAt,
} from './alerts-coordinator-cooldown.storage'
import {
  canRenderAlertCampaign,
  shouldClearSelectedCampaign,
  shouldRecordCoordinatorSeenEvent,
} from './alerts-coordinator.seen-event'
import { ALERT_CAMPAIGN_IDS, ALERT_CAMPAIGN_SEEN_EVENTS } from './alerts.constants'
import type { AlertsCoordinatorProps } from './alerts-coordinator.interface'
import { AlertCampaign, AlertCampaignId } from './alerts.types'
import { AutoReminderModal } from './auto-reminder-modal'
import { FeedbackSurveyModal } from './feedback-survey-modal/feedback-survey-modal'
import { G2CapterraReviewModal } from './g2-capterra-review-modal/g2-capterra-review-modal'
import { InstallChromeExtensionModal } from './install-chrome-extension-modal/install-chrome-extension-modal'
import { KalamaRenamedModal, useRecordKalamaRenamedPopupSeen } from './kalama-renamed-modal'
import { KalamaTeaserModal, useRecordKalamaTeaserPopupSeen } from './kalama-teaser-modal'
import { PausedRemindersModal } from './paused-reminders-modal'
import { ENDED_REMINDERS_MODAL_VIEW_PROPERTIES } from './paused-reminders-modal/paused-reminders-modal.domain'
import { TypeformReviewModal } from './typeform-review-modal/typeform-review-modal'
import { ShopifyWordpressSurveyModal } from './shopify-wordpress-survey-modal/shopify-wordpress-survey-modal'
import { SetAndForgetPromoModal } from './set-and-forget-promo-modal/set-and-forget-promo-modal'
import { useAlertsCoordinator } from './use-alerts-coordinator'

export function AlertsCoordinator({ pausedReminders, isHeldBack = false }: AlertsCoordinatorProps) {
  const { context, isLoading, nextCampaign } = useAlertsCoordinator(pausedReminders)
  const addUserEvent = useAddUserEvent()
  const recordKalamaTeaserPopupSeen = useRecordKalamaTeaserPopupSeen()
  const recordKalamaRenamedPopupSeen = useRecordKalamaRenamedPopupSeen()
  const [currentCampaignId, setCurrentCampaignId] = useState<AlertCampaignId | null>(null)
  const [cooldownRetryTick, setCooldownRetryTick] = useState(0)
  const [pendingSeenCampaignId, setPendingSeenCampaignId] = useState<AlertCampaignId | null>(null)
  const trackedSeenCampaignIdsRef = useRef(new Set<AlertCampaignId>())

  useEffect(() => {
    if (
      isLoading ||
      isHeldBack ||
      currentCampaignId !== null ||
      nextCampaign === null ||
      trackedSeenCampaignIdsRef.current.has(nextCampaign.id) ||
      !canRenderAlertCampaign(nextCampaign.id, context.profile, context.g2CapterraReview.reviewPlatform)
    ) {
      return
    }

    const remainingCooldownMs = getAlertsCoordinatorCooldownRemainingMs()
    if (remainingCooldownMs > 0) {
      const timer = setTimeout(() => setCooldownRetryTick((tick) => tick + 1), remainingCooldownMs)
      return () => clearTimeout(timer)
    }

    setCurrentCampaignId(nextCampaign.id)
  }, [
    context.g2CapterraReview.reviewPlatform,
    context.profile,
    cooldownRetryTick,
    currentCampaignId,
    isHeldBack,
    isLoading,
    nextCampaign,
  ])

  const currentCampaign = useMemo(
    () => alertCampaigns.find((campaign) => campaign.id === currentCampaignId) ?? null,
    [currentCampaignId],
  )

  // The single visibility decision the render below obeys, so seen state can
  // never be spent on a campaign this component does not put on screen.
  const isCampaignVisible = useMemo(
    () =>
      currentCampaignId !== null &&
      !isHeldBack &&
      canRenderAlertCampaign(currentCampaignId, context.profile, context.g2CapterraReview.reviewPlatform),
    [context.g2CapterraReview.reviewPlatform, context.profile, currentCampaignId, isHeldBack],
  )

  // The teaser's own endpoint must write its row first, or it would never send the Amplitude event.
  const recordCampaignSeen = useCallback(
    (campaign: AlertCampaign) => {
      switch (campaign.id) {
        case ALERT_CAMPAIGN_IDS.KALAMA_TEASER:
          if (context.profile !== null) recordKalamaTeaserPopupSeen(context.profile.id)
          return

        case ALERT_CAMPAIGN_IDS.KALAMA_RENAMED:
          if (context.profile !== null) recordKalamaRenamedPopupSeen(context.profile.id)
          return

        default:
          addUserEvent.mutate({ eventName: campaign.seenEventName, once: true })
      }
    },
    [addUserEvent, context.profile, recordKalamaRenamedPopupSeen, recordKalamaTeaserPopupSeen],
  )

  useEffect(() => {
    if (!isCampaignVisible || currentCampaign === null) {
      setPendingSeenCampaignId(null)
      return
    }

    if (
      trackedSeenCampaignIdsRef.current.has(currentCampaign.id) ||
      !shouldRecordCoordinatorSeenEvent(currentCampaign.id)
    ) {
      return
    }

    // Seen state is written `once: true` and never expires, so it is spent only
    // once the modal has survived a commit. A hold-back or a profile change
    // settling right after the selection then cancels it instead of burning it.
    if (pendingSeenCampaignId !== currentCampaign.id) {
      setPendingSeenCampaignId(currentCampaign.id)
      return
    }

    trackedSeenCampaignIdsRef.current.add(currentCampaign.id)
    recordAlertsCoordinatorShownAt()
    recordCampaignSeen(currentCampaign)
  }, [currentCampaign, isCampaignVisible, pendingSeenCampaignId, recordCampaignSeen])

  useEffect(() => {
    if (!shouldClearSelectedCampaign(currentCampaignId, context.profile, context.g2CapterraReview.reviewPlatform)) {
      return
    }

    setCurrentCampaignId(null)
  }, [context.g2CapterraReview.reviewPlatform, context.profile, currentCampaignId])

  const handleClose = useCallback(() => {
    setCurrentCampaignId(null)
  }, [])

  const handleG2CapterraReviewShown = useCallback(() => {
    if (trackedSeenCampaignIdsRef.current.has(ALERT_CAMPAIGN_IDS.G2_CAPTERRA_REVIEW)) {
      return
    }

    trackedSeenCampaignIdsRef.current.add(ALERT_CAMPAIGN_IDS.G2_CAPTERRA_REVIEW)
    recordAlertsCoordinatorShownAt()
    addUserEvent.mutate({
      eventName: ALERT_CAMPAIGN_SEEN_EVENTS[ALERT_CAMPAIGN_IDS.G2_CAPTERRA_REVIEW],
      once: true,
    })
  }, [addUserEvent])

  if (!isCampaignVisible) {
    return null
  }

  switch (currentCampaignId) {
    case ALERT_CAMPAIGN_IDS.INSTALL_CHROME_EXTENSION:
      return <InstallChromeExtensionModal open onClose={handleClose} />

    case ALERT_CAMPAIGN_IDS.PAUSED_REMINDERS:
      if (context.profile === null) {
        return null
      }
      return (
        <PausedRemindersModal
          open
          onClose={handleClose}
          profile={context.profile}
          pausedReminders={context.pausedReminders}
          reason={REMINDERS_PAUSED_REASONS.quota}
        />
      )

    case ALERT_CAMPAIGN_IDS.SUBSCRIPTION_ENDED_REMINDERS:
      if (context.profile === null) {
        return null
      }
      return (
        <PausedRemindersModal
          open
          onClose={handleClose}
          profile={context.profile}
          pausedReminders={context.pausedReminders}
          reason={REMINDERS_PAUSED_REASONS.subscriptionEnded}
          viewProperties={ENDED_REMINDERS_MODAL_VIEW_PROPERTIES}
        />
      )

    case ALERT_CAMPAIGN_IDS.KALAMA_TEASER:
      if (context.profile === null) {
        return null
      }
      return <KalamaTeaserModal open onClose={handleClose} />

    case ALERT_CAMPAIGN_IDS.KALAMA_RENAMED:
      if (context.profile === null) {
        return null
      }
      return <KalamaRenamedModal open onClose={handleClose} />

    case ALERT_CAMPAIGN_IDS.AUTO_REMINDERS:
      return <AutoReminderModal open onClose={handleClose} />

    case ALERT_CAMPAIGN_IDS.SET_AND_FORGET_PROMO:
      return <SetAndForgetPromoModal open onClose={handleClose} />

    case ALERT_CAMPAIGN_IDS.FEEDBACK_SURVEY:
      return <FeedbackSurveyModal open onClose={handleClose} />

    case ALERT_CAMPAIGN_IDS.G2_CAPTERRA_REVIEW:
      if (context.g2CapterraReview.reviewPlatform === null || context.profile === null) {
        return null
      }
      return (
        <G2CapterraReviewModal
          open
          onClose={handleClose}
          onShown={handleG2CapterraReviewShown}
          reviewPlatform={context.g2CapterraReview.reviewPlatform}
          profile={context.profile}
        />
      )

    case ALERT_CAMPAIGN_IDS.TYPEFORM_REVIEW:
      if (context.profile === null) {
        return null
      }
      return <TypeformReviewModal open onClose={handleClose} profile={context.profile} />

    case ALERT_CAMPAIGN_IDS.SHOPIFY_WORDPRESS_SURVEY:
      if (context.profile === null) {
        return null
      }
      return <ShopifyWordpressSurveyModal open onClose={handleClose} profile={context.profile} />

    default:
      return null
  }
}
